import axios from "axios";
import { endPoint } from "../config/endPoint";
import { domainWithHttp } from "../config/onboarding/onBoarding";

/*
 * Application Tracker, onboarding / compliance module (turbo-config-app). The backend records
 * every onboarding call it receives, so the browser only reports what the server cannot see: a
 * call that got no response at all (network, timeout, blocked) or a 5xx that may have come from a
 * proxy rather than Laravel. Called from apiService's catch blocks.
 *
 * Observer only: it never awaits, never modifies the request or the response, never throws, and
 * sends no form answers — only the endpoint, form name, page, status and file name/size.
 */

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const tracedEndpoints: Record<string, string> = {
    [endPoint.getcandidatedata]: "Opening the onboarding link",
    [endPoint.createcandidatedata]: "Saving the form",
    [endPoint.createCandidateDataDocuments]: "Uploading a document",
    [endPoint.checkIsSignedorNot]: "Checking the FLK signature",
    [endPoint.agreement]: "Saving the e-sign agreement",
    [endPoint.deleteFile]: "Removing a document",
};

const read = (params: any, key: string): any => {
    if (!params) {
        return undefined;
    }
    if (typeof FormData !== "undefined" && params instanceof FormData) {
        const value = params.get(key);
        return value === null ? undefined : value;
    }
    return typeof params === "object" ? params[key] : undefined;
};

const short = (value: any, max = 150): string | undefined => {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return String(value).slice(0, max);
};

export const reportOnboardingFailure = (api: string, method: string, params: any, error: any) => {
    try {
        const endpoint = String(api || "").split("?")[0].split("/")[0];
        const label = tracedEndpoints[endpoint];
        const status = Number(error?.response?.status || 0);
        // 4xx always comes from Laravel, which has already recorded it.
        if (!label || !baseUrl || (status !== 0 && status < 500)) {
            return;
        }
        const candidateReferenceId = sessionStorage.getItem("clientId");
        if (!candidateReferenceId) {
            return;
        }

        const file = read(params, "files");
        const formName = short(read(params, "formName") || read(params, "formLabel") || read(params, "flkFormName"));
        const message = status
            ? `${label}${formName ? ` ('${formName}')` : ""} failed in the browser with HTTP ${status}`
            : `${label}${formName ? ` ('${formName}')` : ""} got no response from the server (network, timeout or blocked request)`;

        const payload = {
            module: "compliance",
            candidateReferenceId,
            domain: domainWithHttp,
            events: [{
                step: "request_failed",
                status: "failed",
                message,
                at: Date.now(),
                detail: {
                    endpoint,
                    method,
                    httpStatus: status || null,
                    errorCode: short(error?.code, 50) || null,
                    online: typeof navigator !== "undefined" ? navigator.onLine : null,
                    formName: formName || null,
                    formStatus: short(read(params, "formStatus"), 20) || null,
                    currentPage: short(read(params, "currentPage"), 10) || null,
                    fileName: file && typeof file === "object" && "name" in file ? short(file.name) : null,
                    fileSizeKb: file && typeof file === "object" && "size" in file ? Math.round(file.size / 1024) : null,
                },
            }],
        };
        axios.post(`${baseUrl}${endPoint.applicationTraceEvents}`, payload).catch(() => undefined);
    } catch (_error) {
        // intentionally swallowed: tracing must never affect onboarding
    }
};
